import React from 'react';
import Link from '@amzn/awsui-components-console/link';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import ShowSnowStatus from 'src/components/findEntity/ShowSNOWStatus';
import CopyableText from 'src/components/findEntity/CopyableText';
import { TaskDefinition } from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';
import { getLocaleDateFromEpoch } from 'src/utils/CommonUtils';
import { formatYesNo } from 'src/utils/StringUtils';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';

export const formatExecutionTime = (time: { days: number; hours: number; minutes: number }): string => {
	const parts: string[] = [];

	if (time.days > 0) parts.push(`${time.days}d`);
	if (time.hours > 0) parts.push(`${time.hours}h`);
	if (time.minutes > 0) parts.push(`${time.minutes}m`);

	return parts.length > 0 ? parts.join(' ') : '-';
};

export const createTaskColumns = (showTaskDetailsCallback: (taskDetails?: TaskDefinition) => void) => [
	{
		id: 'taskName',
		header: 'Task Name',
		cell: (item: TaskDefinition) => (
			<Link
				variant="primary"
				onFollow={() => showTaskDetailsCallback(item)}
			>
				{item.taskName}
			</Link>
		),
		sortingField: 'taskName',
	},
	{
		id: 'taskType',
		header: 'Task Type',
		cell: (item: TaskDefinition) => item.taskType || '-',
		sortingField: 'taskType',
	},
	{
		id: 'taskDescription',
		header: 'Description',
		cell: (item: TaskDefinition) => item.taskDescription || '-',
	},
	{
		id: 'owner',
		header: 'Owner',
		cell: (item: TaskDefinition) => item.owner || '-',
		sortingField: 'owner',
	},
	{
		id: 'status',
		header: 'Status',
		cell: (item: TaskDefinition) => item.status ? <ShowSnowStatus status={item.status} /> : '-',
		sortingField: 'status',
	},
	{
		id: 'averageExecutionTime',
		header: 'Avg Execution Time',
		cell: (item: TaskDefinition) => formatExecutionTime(item.averageExecutionTime),
	},
	{
		id: 'asynchronous',
		header: 'Asynchronous',
		cell: (item: TaskDefinition) => (item.asynchronous ? 'Yes' : 'No'),
	},
	{
		id: 'batchExecutionRequired',
		header: 'Batch Execution',
		cell: (item: TaskDefinition) => (item.batchExecutionRequired ? 'Yes' : 'No'),
	},
	{
		id: 'regionalized',
		header: 'Regionalized',
		cell: (item: TaskDefinition) => (item.regionalized ? 'Yes' : 'No'),
	},
	{
		id: 'version',
		header: 'Version',
		cell: (item: TaskDefinition) => item.taskDefinitionVersion || '-',
		sortingField: 'taskDefinitionVersion',
	},
	{
		id: 'createdBy',
		header: 'Created By',
		cell: (item: TaskDefinition) => item.createdBy || '-',
		sortingField: 'createdBy',
	},
	{
		id: 'lastUpdatedBy',
		header: 'Last Updated By',
		cell: (item: TaskDefinition) => item.lastUpdatedBy || '-',
		sortingField: 'lastUpdatedBy',
	},
	{
		id: 'validationDefinitions',
		header: 'Validations',
		cell: (item: TaskDefinition) => {
			const validations = item.validationDefinitions || [];
			if (validations.length === 0) return '-';
			return (
				<SpaceBetween size="xxs">
					{validations.map((v, i) => (
						<Box key={i}>{v.validationName || v.validationId || 'Unnamed'}</Box>
					))}
				</SpaceBetween>
			);
		},
	},
];

export const CONTENT_DISPLAY_OPTIONS = [
	{ id: 'taskName', label: 'Task Name', alwaysVisible: true },
	{ id: 'taskType', label: 'Task Type' },
	{ id: 'taskDescription', label: 'Description' },
	{ id: 'owner', label: 'Owner' },
	{ id: 'status', label: 'Status' },
	{ id: 'averageExecutionTime', label: 'Avg Execution Time' },
	{ id: 'asynchronous', label: 'Asynchronous' },
	{ id: 'batchExecutionRequired', label: 'Batch Execution' },
	{ id: 'regionalized', label: 'Regionalized' },
	{ id: 'version', label: 'Version' },
	{ id: 'createdBy', label: 'Created By' },
	{ id: 'lastUpdatedBy', label: 'Last Updated By' },
	{ id: 'validationDefinitions', label: 'Validations' },
];

export const PAGE_SELECTOR_OPTIONS = [
	{ value: 10, label: '10 tasks' },
	{ value: 20, label: '20 tasks' },
	{ value: 50, label: '50 tasks' },
];

export const COLLECTION_PREFERENCES = {
	title: 'Preferences',
	confirmLabel: 'Confirm',
	cancelLabel: 'Cancel',
	pageSizePreference: {
		title: 'Page size',
		options: PAGE_SELECTOR_OPTIONS,
	},
	wrapLinesPreference: {
		label: 'Wrap lines',
		description: 'Select to see all text and wrap lines',
	},
	stripedRowsPreference: {
		label: 'Striped rows',
		description: 'Select to add alternating shaded rows',
	},
	contentDisplayPreference: {
		title: 'Column preferences',
		options: CONTENT_DISPLAY_OPTIONS,
	},
};

const DEFAULT_VISIBLE = ['taskName', 'taskType', 'taskDescription', 'validationDefinitions', 'regionalized', 'lastUpdatedBy'];

export const DEFAULT_PREFERENCES = {
	pageSize: 10,
	contentDisplay: CONTENT_DISPLAY_OPTIONS.map(({ id }) => ({
		id,
		visible: DEFAULT_VISIBLE.includes(id),
	})),
	wrapLines: true,
	stripedRows: getDefaultStripedRows(),
};

export const WORKFLOW_DETAILS_METADATA_MAP = (workflowData: {
	workflowDefinitionName: string;
	workflowId: string;
	status: string;
	description: string;
	domains: string[];
	entityType: string;
	createdAt: number;
	createdBy: string;
	lastUpdatedBy: string;
	derivedFromId?: string;
	usableAsSubWorkflow?: boolean;
}) => [
	{ label: 'Workflow Definition Name', value: workflowData.workflowDefinitionName },
	{ label: 'Workflow Id', value: <CopyableText textToCopy={workflowData.workflowId} label="Workflow Id">{workflowData.workflowId}</CopyableText> },
	{ label: 'Status', value: <ShowSnowStatus status={workflowData.status} /> },
	{ label: 'Description', value: workflowData.description },
	{
		label: 'Domains',
		value: workflowData.domains,
	},
	{ label: 'Entity Type', value: workflowData.entityType },
	{ label: 'Can Be A Sub-workflow', value: formatYesNo(Boolean(workflowData.usableAsSubWorkflow)) },
	{ label: 'Created At', value: getLocaleDateFromEpoch(workflowData.createdAt) },
	{ label: 'Created By', value: workflowData.createdBy },
	{ label: 'Last Updated By', value: workflowData.lastUpdatedBy },
	...(workflowData.derivedFromId ? [{ label: 'Derived From Workflow', value: workflowData.derivedFromId }] : []),
];